import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "/kf-logo.png";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Products", path: "/products" },
  { label: "Capabilities", path: "/capabilities" },
  { label: "Process", path: "/process" },
  { label: "Global Presence", path: "/global-presence" },
];

const slug = (label) => label.toLowerCase().replace(/\s/g, "-");

function DesktopNav({ pathname }) {
  return (
    <>
      <div className="hidden lg:flex items-center gap-8">
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            data-testid={`nav-link-${slug(link.label)}`}
            className={`text-sm font-sans font-medium tracking-wide uppercase transition-colors duration-200 ${
              pathname === link.path ? "text-gold" : "text-foreground/70 hover:text-foreground"
            }`}
          >
            {link.label}
          </Link>
        ))}
      </div>
      <div className="hidden lg:flex items-center gap-4">
        <Link to="/contact">
          <Button
            data-testid="nav-contact-btn"
            className="bg-[#0F172A] text-white rounded-none px-6 py-5 text-xs uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#0F172A]/90"
          >
            Get in Touch
          </Button>
        </Link>
      </div>
    </>
  );
}

function MobileMenu({ pathname }) {
  return (
    <div data-testid="mobile-menu" className="lg:hidden bg-background border-t border-border">
      <div className="px-6 py-8 space-y-6">
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            data-testid={`mobile-nav-link-${slug(link.label)}`}
            className={`block font-serif text-2xl ${pathname === link.path ? "text-gold" : "text-foreground"}`}
          >
            {link.label}
          </Link>
        ))}
        <Link to="/contact" className="block pt-4">
          <Button data-testid="mobile-nav-contact-btn" className="w-full bg-[#0F172A] text-white rounded-none py-5 text-xs uppercase tracking-[0.15em] font-sans">
            Get in Touch
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass-nav-scrolled" : "glass-nav"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="KF Prints" className="h-[46px] lg:h-[55px] w-auto" />
          </Link>
          <DesktopNav pathname={location.pathname} />
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {mobileOpen && <MobileMenu pathname={location.pathname} />}
    </nav>
  );
}
